import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { formatDate } from '../../utils/formatDate';
import { fetchBlogPosts } from '../../utils/fetchBlogPosts';
import { useI18n } from '../../i18n';
import { en, km } from './[slug].copy';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { locale, localePath } = useI18n();
  const t = locale === 'km' ? km : en;
  const [post, setPost] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogPost = async () => {
      const posts = await fetchBlogPosts(locale);
      setBlogPosts(posts);

      // Find the post with the matching slug
      const foundPost = posts.find(p => p.slug === slug);
      setPost(foundPost || null);
      setLoading(false);
    };

    loadBlogPost();
  }, [slug, locale]);
  
  // If still loading, show a loading message
  if (loading) {
    return (
      <Layout canonicalPath={`/blog/${slug}`}>
        <main>
          <div className="container mx-auto py-12 text-center">
            <h2 className="heading-medium">{t.loading}</h2>
          </div>
        </main>
      </Layout>
    );
  }
  
  // If no post is found, show a 404 message
  if (!post) {
    return (
      <Layout canonicalPath={`/blog/${slug}`} noIndex>
        <main>
          <div className="flex items-center justify-center bg-gray-50 w-full h-full py-20">
            <div className="text-center lg:max-w-lg px-4">
              <div className="mb-6">
                <p className="text-9xl lg:text-xxl font-bold text-black opacity-10">404</p>
              </div>
              <h4 className="mb-2">{t.notFoundTitle}</h4>
              <p className="text-label text-gray-500 mb-6">
                {t.notFoundBody}
              </p>
              <Link to={localePath('/blog')} className="button primary" style={{ backgroundColor: "#1766FF", color: "#FFFFFF" }}>
                {t.backToBlog}
              </Link>
            </div>
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout
      title={`${post.title} | ${t.layoutTitleSuffix}`}
      description={post.excerpt || `${post.title} - ${t.fallbackDescription}`}
      canonicalPath={`/blog/${slug}`}
      image={post.image ? `https://akas.co${post.image}` : undefined}
      ogType="article"
    >
      <main>
        <section className="relative">
          <div className="container mx-auto py-12 lg:py-14 xl:py-16">
            <div className="mb-6">
              <Link to={localePath('/blog')} className="mt-5 inline-flex items-center text-primary hover:underline">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24" className="w-4 h-4 mr-2">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m15 19-7-7 7-7"></path>
                </svg>
                {t.backToBlogs}
              </Link>
            </div>
            
            <h1 className="heading-large mb-6 text-primary" >{post.title}</h1>
            
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-500">
                <span className="body-normal">{t.byAuthor} {post.author}</span>
              </div>
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
              <div className="flex items-center gap-2 text-gray-500">
                <span className="body-normal">{formatDate(post.publishedAt, locale)}</span>
              </div>
            </div>
            
            {/* <div className="mb-10">
              <div 
                className="w-full h-96 md:h-[500px] bg-cover bg-center rounded-lg" 
                style={{ backgroundImage: `url(${post.image})` }}
              />
            </div> */}
            
            <div className="max-w-4xl mx-auto">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </div>

            <div className="border-t border-gray-200 mt-16 pt-16">
              <h3 className="heading-medium mb-8">{t.readMore}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogPosts
                  .filter(p => p.slug !== slug)
                  .slice(0, 3)
                  .map(relatedPost => (
                    <Link 
                      key={relatedPost.id} 
                      to={localePath(`/blog/${relatedPost.slug}`)} 
                      className="group"
                    >
                      {/* <div 
                        className="w-full h-48 bg-cover bg-center rounded-lg mb-4 transition-transform group-hover:scale-105"
                        style={{ backgroundImage: `url(${relatedPost.image})` }}
                      ></div> */}
                      <h4 className="text-lg font-medium">{relatedPost.title}</h4>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
} 